import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonLabel,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_PUBLIC_USER_PROFILE } from '../../../graphql/queries/publicProfiles';
import type {
  GetPublicUserProfileQuery,
  GetPublicUserProfileQueryVariables,
} from '../../../types/graphql-generated';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { FeatureFlag } from '../../../types/featureFlags';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';
import { ChannelListItem } from '../channels/components/ChannelListItem';
import { PublicProfileCard } from './components/PublicProfileCard';
import { PublicCareerHistorySection } from './components/PublicCareerHistorySection';
import { publicProfileName } from './publicProfileName';

interface PublicProfilePageProps {
  defaultBackHref?: string;
  channelPathPrefix?: string;
  overlapsPathPrefix?: string;
}

// Public profile of another user: identity block, the channels they own (to
// follow them) and their career history. Route prefixes keep the shared page in
// whichever tab stack opened it.
export const PublicProfilePage = ({
  defaultBackHref = '/app/main/perfil',
  channelPathPrefix = '/app/main/perfil/canal',
  overlapsPathPrefix = '/app/main/perfil/experiencia',
}: PublicProfilePageProps) => {
  const userId = useParams<'userId'>().userId!;
  const { isEnabled } = useFeatureFlags();
  const { data, loading, error } = useQuery<
    GetPublicUserProfileQuery,
    GetPublicUserProfileQueryVariables
  >(GET_PUBLIC_USER_PROFILE, { variables: { id: userId } });

  const profile = data?.publicUserProfile;

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={defaultBackHref} />
          </IonButtons>
          <IonTitle className="ml-4">{profile ? publicProfileName(profile) : 'Perfil'}</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {loading && !profile && (
          <div className="flex min-h-[200px] items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !profile && (
          <IonText color="danger">
            <p>Error al cargar el perfil</p>
          </IonText>
        )}

        {!loading && !error && !profile && (
          <div className="flex min-h-[200px] items-center justify-center">
            <IonText color="medium">
              <p>No se ha encontrado el perfil.</p>
            </IonText>
          </div>
        )}

        {profile && (
          <>
            <PublicProfileCard profile={profile} />

            {isEnabled(FeatureFlag.POSTS) && profile.channels.length > 0 && (
              <div className="mt-4">
                <IonLabel className="mb-2 block text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Canales
                </IonLabel>
                {profile.channels.map((channel) => (
                  <ChannelListItem
                    key={channel.id}
                    id={channel.id}
                    name={channel.name}
                    description={channel.description}
                    viewerIsOwner={channel.viewerIsOwner}
                    viewerIsFollowing={channel.viewerIsFollowing}
                    channelPath={`${channelPathPrefix}/${channel.id}`}
                  />
                ))}
              </div>
            )}

            <PublicCareerHistorySection
              entries={profile.careerEntries}
              overlapsPathPrefix={overlapsPathPrefix}
            />
          </>
        )}
      </IonContent>
    </IonPage>
  );
};
